/**
 * Хук для работы с данными формы оплаты в localStorage
 * 
 * Автоматически сохраняет и загружает данные формы оплаты для неавторизованных пользователей.
 * Данные включают: номер телефона, сумму пополнения и email (опционально).
 * Использует debounce для оптимизации записи в localStorage.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { config } from '../config';

/**
 * Интерфейс данных формы оплаты для хранения в localStorage
 */
export interface PaymentStorageData {
  phone?: string;
  amount?: number;
  email?: string;
}

/**
 * Ключ для хранения данных в localStorage (из конфигурации)
 */
const STORAGE_KEY = config.storage.keys.paymentFormData;

/**
 * Задержка для debounce в миллисекундах
 */
const DEBOUNCE_DELAY = 300;

/**
 * Загрузка данных из localStorage
 * 
 * @returns Данные формы оплаты или null, если данных нет
 */
const loadFromStorage = (): PaymentStorageData | null => {
  try {
    if (typeof window === 'undefined') {
      return null;
    }

    const stored = localStorage.getItem(STORAGE_KEY);
    
    if (!stored) {
      return null;
    }
    
    return JSON.parse(stored) as PaymentStorageData;
  } catch (error) {
    console.error('Error loading payment data from localStorage:', error);
    return null;
  }
};

/**
 * Сохранение данных в localStorage
 * 
 * @param data - Данные формы оплаты для сохранения
 */
const saveToStorage = (data: PaymentStorageData): void => {
  try {
    if (typeof window === 'undefined') {
      return;
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving payment data to localStorage:', error);
  }
};

/**
 * Очистка данных из localStorage
 */
const clearStorage = (): void => {
  try {
    if (typeof window === 'undefined') {
      return;
    }

    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing payment data from localStorage:', error);
  }
};

/**
 * Результат хука usePaymentStorage
 */
export interface UsePaymentStorageResult {
  /** Сохраненные данные формы */
  data: PaymentStorageData;
  /** Функция для обновления данных */
  updateData: (newData: Partial<PaymentStorageData>) => void;
  /** Функция для очистки данных */
  clearData: () => void;
  /** Флаг загрузки данных */
  isLoaded: boolean;
}

/**
 * Хук для работы с данными формы оплаты в localStorage
 * 
 * Автоматически загружает данные при монтировании компонента и
 * предоставляет функции для обновления и очистки данных.
 * Использует debounce (300ms) для оптимизации записи в localStorage.
 * 
 * @returns Объект с данными и функциями для работы с ними
 * 
 * @example
 * ```tsx
 * const { data, updateData, clearData } = usePaymentStorage();
 * 
 * // Обновление телефона (с debounce)
 * updateData({ phone: '+79001234567' });
 * 
 * // Обновление суммы (с debounce)
 * updateData({ amount: 500 });
 * 
 * // Очистка всех данных
 * clearData();
 * ```
 */
export const usePaymentStorage = (): UsePaymentStorageResult => {
  const [data, setData] = useState<PaymentStorageData>({});
  const [isLoaded, setIsLoaded] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Загрузка данных при монтировании
  useEffect(() => {
    const storedData = loadFromStorage();
    
    if (storedData) {
      setData(storedData);
    }
    
    setIsLoaded(true);
  }, []);

  // Очистка таймера при размонтировании
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  /**
   * Обновление данных в состоянии и localStorage с debounce
   * Данные сохраняются в localStorage с задержкой 300ms для оптимизации
   */
  const updateData = useCallback((newData: Partial<PaymentStorageData>) => {
    setData((prevData) => {
      const updatedData = { ...prevData, ...newData };
      
      // Очищаем предыдущий таймер
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      // Устанавливаем новый таймер для сохранения
      debounceTimerRef.current = setTimeout(() => {
        saveToStorage(updatedData);
      }, DEBOUNCE_DELAY);
      
      return updatedData;
    });
  }, []);

  /**
   * Очистка данных из состояния и localStorage
   * Выполняется немедленно без debounce
   */
  const clearData = useCallback(() => {
    // Отменяем отложенное сохранение
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    setData({});
    clearStorage();
  }, []);

  return {
    data,
    updateData,
    clearData,
    isLoaded,
  };
};
